import { ChangeDetectorRef, Component, inject, signal } from '@angular/core';
import { BasePageComponent } from '../pages-base/base-page.component';
import { AddProductToMonthParams, AddReviewerToProduct, PlanFactApiService } from './services/plan-fact-api.service';
import {
  PlanFactTableDataGroupedByMonths,
  Product,
  ProductOption,
  ToggleColumnAction,
  UpdateColumnStateParams,
  UpdateValueParams
} from './types/table-data.types';
import { MatButton } from '@angular/material/button';
import { dataGroupedByMonths } from './configs/month.config';
import { defaultDeleteConfirmationParams } from '../../../core/models/default-delete-confirmation-params';
import { ConfirmService } from '../../../core/services/confirm.service';
import { MatOption } from '@angular/material/autocomplete';
import { MatFormField, MatSelect, MatSelectChange } from '@angular/material/select';
import { FormsModule } from '@angular/forms';
import { ClickOutsideDirective } from "../../../shared/directives/click-outside.directive";
import { platformNames } from "./configs/default-platforms.config";
import { columnsConfig } from "./configs/columns.config";
import { PlanFactService } from "./services/plan-fact.service";
import { DeleteFocusDirective } from "../../../shared/directives/delete-focus.directive";
import { smoothShowHideHeight } from "../../../shared/animations/smoothShowHideAnimations";
import { FactHeaderComponent } from "./components/fact-header/fact-header.component";
import {
  GroupedHeaderColumnsComponent
} from "./components/grouped-header-columns/grouped-header-columns.component";
import {
  PlanFactReviewerRowComponent
} from "./components/plan-fact-reviewer-row/plan-fact-reviewer-row.component";
import {
  PlanFactManagersRowsComponent
} from "./components/plan-fact-managers-rows/plan-fact-managers-rows.component";
import {
  PlanFactPlatformComponent
} from "./components/plan-fact-platform/plan-fact-platform.component";
import { LoaderComponent } from "../../../shared/components/loader/loader.component";
import { PageInfoComponent } from "../../../core/components/page-info/page-info.component";

@Component({
  selector: 'app-plan-fact-page',
  standalone: true,
  imports: [
    MatButton,
    MatOption,
    MatSelect,
    FormsModule,
    MatFormField,
    ClickOutsideDirective,
    DeleteFocusDirective,
    FactHeaderComponent,
    GroupedHeaderColumnsComponent,
    PlanFactReviewerRowComponent,
    PlanFactManagersRowsComponent,
    PlanFactPlatformComponent,
    LoaderComponent,
    PageInfoComponent
  ],
  templateUrl: './plan-fact-page.component.html',
  styleUrls: [
    './plan-fact-page.component.scss',
    'components/plan-fact-components-styles.scss'
  ],
  animations: [ smoothShowHideHeight ]
})
export class PlanFactPageComponent extends BasePageComponent {
  private planFactApiService: PlanFactApiService = inject(PlanFactApiService);
  private confirmService: ConfirmService = inject(ConfirmService);
  private changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);
  private planFactService: PlanFactService = inject(PlanFactService);

  public readonly columnsConfig = columnsConfig;
  public readonly platformNames = platformNames;
  public dataGroupedByMonths = signal<Array<PlanFactTableDataGroupedByMonths>>(dataGroupedByMonths);
  public selectedYear = signal<number>(new Date().getFullYear());
  public isLoadingMonth = signal<Record<number, boolean>>({});
  public years: Array<number> = [2024, 2025, 2026, 2027, 2028];

  public isAnyTeamInProduct(product: Product): boolean {
    return !!product.teams.length;
  }

  public handleSelectChange(selectChange: MatSelectChange): void {
    this.selectedYear.set(selectChange.value);
    this.closeAllMonths();
  }

  public async openUnAddedProductsList(monthData: PlanFactTableDataGroupedByMonths): Promise<void> {
    monthData.isUnAddedProductsListOpen = true;
    const allProductsOptions: Array<ProductOption> = await this.getProductOptions(monthData.monthNumber);
    const addedProductsOptions = monthData.planFactData?.products ?? [];
    monthData.unAddedProductsList = allProductsOptions.filter((productOption: ProductOption) => {
      return !addedProductsOptions.map(p => p.productId).includes(productOption.productId);
    });
  }

  public closeUnAddedProductsList(monthData: PlanFactTableDataGroupedByMonths): void {
    monthData.isUnAddedProductsListOpen = false;
    monthData.unAddedProductsList = [];
  }

  public toggleUnAddedProductsList(monthData: PlanFactTableDataGroupedByMonths): void {
    monthData.isUnAddedProductsListOpen ? this.closeUnAddedProductsList(monthData) : this.openUnAddedProductsList(monthData);
  }

  public async openUnAddedReviewerList(product: Product): Promise<void> {
    product.isUnAddedReviewerListOpen = true;
  }

  public closeUnAddedReviewerList(product: Product): void {
    product.isUnAddedReviewerListOpen = false;
  }

  public toggleUnAddedReviewerList(product: Product): void {
    product.isUnAddedReviewerListOpen ? this.closeUnAddedReviewerList(product) : this.openUnAddedReviewerList(product);
  }

  public toggleActionsColumn(toggleColumnAction: ToggleColumnAction): void {
    const { monthNumber, productId, columnType, platformType } = toggleColumnAction;
    const monthData = dataGroupedByMonths.find((month) => month.monthNumber === monthNumber);
    if (!monthData?.planFactData?.products) { return; }

    const productIndex = monthData.planFactData.products.findIndex((product) => product.id === productId);
    if (productIndex === -1) { return; }

    this.updateColumnState({
      products: monthData.planFactData.products,
      productIndex,
      platformType,
      columnType,
    });
    this.changeDetectorRef.detectChanges();
  }

  public addProductToMonth(monthData: PlanFactTableDataGroupedByMonths, productId: number): void {
    this.closeUnAddedProductsList(monthData);
    const params: AddProductToMonthParams = {
      productId,
      month: monthData.monthNumber,
      year: this.selectedYear()
    }

    this.planFactApiService.addProductToMonth(params).subscribe({
      next: () => {
        this.planFactService.openMonthData({
          monthNumber: monthData.monthNumber,
          selectedYear: this.selectedYear()
        }).then()
      }
    })
  }

  public addReviewerToProduct(product: Product, reviewerId: number, monthNumber: number): void {
    this.closeUnAddedReviewerList(product);

    const params: AddReviewerToProduct = {
      productPlanFactId: product.id,
      reviewerId: reviewerId
    }

    this.planFactApiService.addReviewerToProduct(params).subscribe({
      next: () => {
        this.planFactService.openMonthData({
          monthNumber: monthNumber,
          selectedYear: this.selectedYear()
        }).then()
      }
    })
  }

  public async openMonthData(monthNumber: number): Promise<void> {
    this.isLoadingMonth.set({[monthNumber]: true});
    await this.planFactService.openMonthData({
      monthNumber: monthNumber,
      selectedYear: this.selectedYear()
    }).finally(() => this.isLoadingMonth.set({}));
  }

  public closeMonthData(monthNumber: number): void {
    const changedMonthIndex = this.planFactService.getIndexForChangedMonth(monthNumber);
    this.dataGroupedByMonths()[changedMonthIndex].planFactData = null;
  }

  public closeAllMonths(): void {
    for (let i = 1; i < 12; i++) {
      this.closeMonthData(i);
    }
  }

  public async handleDeleteProductButtonClick(id: number, monthNumber: number): Promise<void> {
    const isConfirmed = await this.confirmService.getDeleteConfirmation({
      ...defaultDeleteConfirmationParams,
      titleText: 'Удалить продукт',
      description: '<p> Вы уверены, что хотите удалить продукт?</p> <p>Все руководители и менеджеры также будут удалены.</p>'
    });
    if (!isConfirmed) { return; }

    this.planFactApiService.deleteProduct(id).subscribe({
      next: () => {
        this.planFactService.openMonthData({
          monthNumber: monthNumber,
          selectedYear: this.selectedYear()
        }).then()
      }
    });
  }

  public updateValue(updateValueParams: UpdateValueParams): void {
    this.planFactService.updateValue(updateValueParams, this.selectedYear());
  }

  private async getProductOptions(monthNumber: number): Promise<Array<ProductOption>> {
    return new Promise((resolve, reject) => {
      this.planFactApiService.getProductOptions(monthNumber, this.selectedYear()).subscribe({
        next: (productOptions: Array<ProductOption>) => resolve(productOptions),
        error: (error) => reject(error)
      })
    })
  }

  private updateColumnState(updateColumnStateParams: UpdateColumnStateParams): void {
    const { products, productIndex, platformType, columnType } = updateColumnStateParams;
    const product = products[productIndex];

    products[productIndex] = {
      ...product,
      platforms: {
        ...product.platforms,
        [platformType]: {
          ...product.platforms[platformType],
          [columnType]: !product.platforms[platformType][columnType],
        },
      },
    };
  }
}
